import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ── Result types ───────────────────────────────────────────────────────────────

export type SearchResultType = 'contact' | 'campaign' | 'event';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle: string;
  href: string;
  meta?: string; // badge text
  metaColor?: string; // tailwind class
}

// ── Main hook ──────────────────────────────────────────────────────────────────

export function useGlobalSearch(query: string, enabled: boolean) {
  const q = query.trim();

  return useQuery({
    queryKey: ['global-search', q],
    queryFn: () => fetchAll(q),
    enabled: enabled && q.length >= 1,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

// ── Fetchers ───────────────────────────────────────────────────────────────────

async function fetchAll(q: string): Promise<SearchResult[]> {
  const [contacts, campaigns, events] = await Promise.all([
    fetchContacts(q),
    fetchCampaigns(q),
    fetchEvents(q),
  ]);
  return [...contacts, ...campaigns, ...events];
}

async function fetchContacts(q: string): Promise<SearchResult[]> {
  const { data } = await (supabase as any)
    .from('contacts')
    .select('id, full_name, phone, category')
    .or(`full_name.ilike.%${q}%,phone.ilike.%${q}%`)
    .eq('active', true)
    .limit(4);

  return (data ?? []).map((c: { id: string; full_name: string; phone: string | null; category: string | null }) => ({
    id:       c.id,
    type:     'contact' as const,
    title:    c.full_name,
    subtitle: c.phone ?? 'Sem telefone',
    href:     `/app/contatos/${c.id}`,
    meta:     c.category ?? undefined,
    metaColor: 'bg-blue-500/15 text-blue-300',
  }));
}

async function fetchCampaigns(q: string): Promise<SearchResult[]> {
  const { data } = await (supabase as any)
    .from('campaigns')
    .select('id, name, type, status')
    .ilike('name', `%${q}%`)
    .limit(4);

  const statusLabel: Record<string, string> = {
    draft: 'Rascunho', scheduled: 'Agendada', running: 'Enviando',
    paused: 'Pausada', completed: 'Concluída', failed: 'Falha',
  };
  const statusColor: Record<string, string> = {
    draft: 'bg-slate-500/15 text-slate-300',
    scheduled: 'bg-blue-500/15 text-blue-300',
    running: 'bg-amber-500/15 text-amber-300',
    completed: 'bg-emerald-500/15 text-emerald-300',
    failed: 'bg-danger/15 text-danger',
    paused: 'bg-slate-500/15 text-slate-300',
  };
  const typeLabel: Record<string, string> = { whatsapp: 'WhatsApp', email: 'E-mail', sms: 'SMS' };

  return (data ?? []).map((c: { id: string; name: string; type: string; status: string }) => ({
    id:        c.id,
    type:      'campaign' as const,
    title:     c.name,
    subtitle:  typeLabel[c.type] ?? c.type,
    href:      `/app/campanhas/${c.id}`,
    meta:      statusLabel[c.status],
    metaColor: statusColor[c.status],
  }));
}

async function fetchEvents(q: string): Promise<SearchResult[]> {
  const { data } = await (supabase as any)
    .from('events')
    .select('id, title, starts_at, status, type')
    .ilike('title', `%${q}%`)
    .neq('status', 'cancelled')
    .order('starts_at', { ascending: true })
    .limit(4);

  return (data ?? []).map((e: { id: string; title: string; starts_at: string; status: string; type: string }) => ({
    id:        e.id,
    type:      'event' as const,
    title:     e.title,
    subtitle:  new Date(e.starts_at).toLocaleDateString('pt-BR', {
                 weekday: 'short', day: 'numeric', month: 'short',
               }),
    href:      `/app/eventos/${e.id}`,
    meta:      e.status === 'ongoing' ? 'Em andamento' : undefined,
    metaColor: 'bg-amber-500/15 text-amber-300',
  }));
}
